import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  LIBRARY_TYPES,
  filterLibraryDocuments,
  libraryScopeTotal,
} from '../models/LibraryModel';

/**
 * ViewModel for the Library page (PRD-1605). Subscribes to the shared
 * LibraryModel and derives what the page shows: the visible documents, the
 * count line's total, and whether the open type has any documents at all.
 * PRD-1606 adds the Add document draft, saving, and Retry; PRD-1607 archiving.
 */
export const useLibraryViewModel = (model) => {
  const [state, setState] = useState(() => model.getState());

  useEffect(() => {
    setState(model.getState());
    return model.subscribe(setState);
  }, [model]);

  const { documents, type, filters } = state;

  const visibleDocuments = useMemo(
    () => filterLibraryDocuments(documents, type, filters),
    [documents, type, filters],
  );
  const scopeTotal = useMemo(
    () => libraryScopeTotal(documents, type, filters.visibility),
    [documents, type, filters.visibility],
  );
  const typeHasDocuments = useMemo(
    () => documents.some((d) => d.type === type),
    [documents, type],
  );

  const selectType = useCallback((next) => model.setType(next), [model]);
  const selectView = useCallback((next) => model.setView(next), [model]);
  const setFilter = useCallback((key, value) => model.setFilter(key, value), [model]);
  const clearSearchAndFilters = useCallback(() => model.clearSearchAndFilters(), [model]);
  const setDraft = useCallback((patch) => model.setDraft(patch), [model]);
  const addDocument = useCallback((fields) => model.addDocument(fields), [model]);
  const retry = useCallback((id) => model.retry(id), [model]);
  const archive = useCallback((id) => model.archive(id), [model]);
  const unarchive = useCallback((id) => model.unarchive(id), [model]);
  // The document as it is now, not as some render last saw it.
  const findDocument = useCallback((id) => model.getState().documents.find((d) => d.id === id) || null, [model]);

  return {
    type,
    typeMeta: LIBRARY_TYPES[type],
    view: state.view,
    filters,
    visibleDocuments,
    scopeTotal,
    typeHasDocuments,
    selectType,
    selectView,
    setFilter,
    clearSearchAndFilters,
    draft: state.draft,
    setDraft,
    addDocument,
    retry,
    archive,
    unarchive,
    findDocument,
  };
};
